/**
 * shopify-cart-sync.js — v4.8.1
 *
 * Shopify storefront cart sync.
 *
 * USER ID RESOLUTION (no hardcoded IDs):
 *   1. ?uId= or ?userId= in URL  → save to localStorage, use it
 *   2. localStorage cs_user_id   → already have one, use it
 *   3. Neither                   → auto-generate UUIDv4, save to localStorage
 *
 * On arrival from WP checkout URL (?uId=<uuid>):
 *   1. Save userId from URL to localStorage
 *   2. Stamp cs_user_id onto Shopify cart attributes
 *   3. Fetch cart from DO via HTTP GET /cart?userId=<uuid>
 *   4. Apply if DO is newer/different
 *   5. Connect WebSocket for ongoing sync
 *
 * Three-layer loop prevention:
 *   1. sourceId      — ignore messages we sent ourselves
 *   2. cartHash      — ignore if incoming cart state is identical
 *   3. lastUpdatedAt — ignore if incoming is older than our last update
 *
 * Cart update method: clear + add (guarantees exact DO cart state, no stale diffs).
 * UI refresh method:  Dawn Section Rendering API — no page reload.
 * Toast notifications: shown after every background cart update so the user is aware.
 *
 * v4.8.1 — Checkout button safety timeout (5 s):
 *   Whenever the checkout button enters its loading/syncing state a 5-second
 *   watchdog timer is started. If sync hasn't finished by then (e.g. a 422
 *   variant error exhausted retries, or a network hang) the button is
 *   force-restored so the user can always proceed to checkout.
 *
 * v4.8.0 — Non-blocking sync + diff-based cart apply:
 *   Removed the full-page arrival overlay. Page renders immediately.
 *   A spinner on checkout button(s) + thin sync strip indicate progress.
 *   _doApply fetches the current Shopify cart first and only sends API
 *   calls for items that actually changed (add / qty-update / remove).
 *
 * v4.6.0 — Variant error auto-retry (no page reload):
 *   422/404 from /cart/add.js → wait 1.5 s, re-fetch DO cart, re-apply.
 *   After MAX_APPLY_RETRIES give up and show error toast.
 */
(function () {
  'use strict';

  var LOG = {
    info:  function (m, d) { d !== undefined ? console.log('%c[Shopify-CartSync] ' + m, 'color:#2980b9;font-weight:bold;', d)  : console.log('%c[Shopify-CartSync] ' + m, 'color:#2980b9;font-weight:bold;'); },
    ok:    function (m, d) { d !== undefined ? console.log('%c[Shopify-CartSync] OK ' + m, 'color:#27ae60;font-weight:bold;', d) : console.log('%c[Shopify-CartSync] OK ' + m, 'color:#27ae60;font-weight:bold;'); },
    warn:  function (m, d) { d !== undefined ? console.warn('%c[Shopify-CartSync] WARN ' + m, 'color:#e67e22;font-weight:bold;', d) : console.warn('%c[Shopify-CartSync] WARN ' + m, 'color:#e67e22;font-weight:bold;'); },
    error: function (m, d) { d !== undefined ? console.error('%c[Shopify-CartSync] ERR ' + m, 'color:#e74c3c;font-weight:bold;', d) : console.error('%c[Shopify-CartSync] ERR ' + m, 'color:#e74c3c;font-weight:bold;'); },
    push:  function (m, d) { d !== undefined ? console.log('%c[Shopify-CartSync] >> ' + m, 'color:#8e44ad;font-weight:bold;', d) : console.log('%c[Shopify-CartSync] >> ' + m, 'color:#8e44ad;font-weight:bold;'); },
    recv:  function (m, d) { d !== undefined ? console.log('%c[Shopify-CartSync] << ' + m, 'color:#16a085;font-weight:bold;', d) : console.log('%c[Shopify-CartSync] << ' + m, 'color:#16a085;font-weight:bold;'); },
  };

  // ── CONFIG ────────────────────────────────────────────────────────────
  var WORKER_URL = 'https://cart-sync-do.applabs.workers.dev';
  var WP_DOMAIN  = 'https://cercacultivation.com';

  // ── USER ID RESOLUTION ─────────────────────────────────────────────────
  var LS_USER_ID_KEY = 'cs_user_id';

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function isValidUUID(str) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);
  }

  var _urlParams = new URLSearchParams(window.location.search);

  // Read ?uId= or ?userId= from URL
  var URL_USER_ID = _urlParams.get('uId') || _urlParams.get('userId') || null;

  // ── v4.6.0: retry counter ─────────────────────────────────────────────
  var CS_RETRY    = parseInt(_urlParams.get('cs_retry') || '0', 10);
  var MAX_RETRIES = 2;

  // Resolve USER_ID: URL > localStorage > auto-generate
  var USER_ID = (function () {
    if (URL_USER_ID && isValidUUID(URL_USER_ID)) {
      var existing = localStorage.getItem(LS_USER_ID_KEY);
      if (existing !== URL_USER_ID) {
        LOG.ok('URL userId received — saving to localStorage old:' + (existing || 'none') + ' new:' + URL_USER_ID);
        localStorage.setItem(LS_USER_ID_KEY, URL_USER_ID);
      } else {
        LOG.info('URL userId matches localStorage — no change');
      }
      return URL_USER_ID;
    }
    var stored = localStorage.getItem(LS_USER_ID_KEY);
    if (stored && isValidUUID(stored)) {
      LOG.info('Using existing userId from localStorage: ' + stored);
      return stored;
    }
    var fresh = generateUUID();
    localStorage.setItem(LS_USER_ID_KEY, fresh);
    LOG.ok('Auto-generated new userId: ' + fresh);
    return fresh;
  })();

  var WS_URL          = WORKER_URL.replace(/^http/, 'ws') + '/ws';
  var HTTP_UPDATE_URL = WORKER_URL + '/cart/update';
  var SOURCE_ID       = 'shopify_' + Math.random().toString(36).substring(2, 10);

  LOG.ok('Loaded | userId:' + USER_ID + ' sourceId:' + SOURCE_ID + ' cs_retry:' + CS_RETRY);

  // ── State ─────────────────────────────────────────────────────────────
  var ws             = null;
  var reconnectDelay = 2000;
  var isStopped      = false;
  var syncDebounce   = null;

  var isSyncingFromDO      = false;
  var pendingApply         = null;
  var isApplying           = false;
  var SYNC_COOLDOWN_MS     = 500;
  var lastApplyCompletedAt = 0;

  // ── v4.6.0: in-memory retry counter for variant errors ────────────────
  var _applyRetryCount  = 0;
  var MAX_APPLY_RETRIES = 2;

  // ── Cart hash (djb2) — MUST be identical to WP side ──────────────────
  function computeCartHash(items) {
    if (!items || items.length === 0) return 'empty';
    var str = items
      .map(function (i) {
        var vid = i.variant_id !== undefined ? i.variant_id : (i.variantId || '');
        return String(vid) + ':' + String(i.quantity);
      })
      .sort()
      .join('|');
    return djb2(str);
  }

  function djb2(str) {
    var hash = 5381;
    for (var i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
      hash = hash >>> 0;
    }
    return hash.toString(16);
  }

  function nowSeconds() { return Math.floor(Date.now() / 1000); }
  function getLocalTs()   { return parseInt(localStorage.getItem('cs_cart_updated_at') || '0', 10); }
  function getLocalHash() { return localStorage.getItem('cs_cart_hash') || ''; }

  function setLocalMeta(ts, hash) {
    localStorage.setItem('cs_cart_updated_at', String(ts));
    localStorage.setItem('cs_cart_hash', hash);
    LOG.info('Local meta updated ts:' + ts + ' hash:' + hash);
  }

  // ── v4.8.1: Checkout button loading state ────────────────────────────
  // Shows a spinner on the checkout button(s) while the DO cart is being
  // applied. A 5-second safety watchdog always restores the button so the
  // user is never permanently blocked (e.g. after a 422 retry exhaustion
  // or an unexpected network hang).

  var _checkoutBtnSafetyTimer = null;
  var CHECKOUT_BTN_SAFETY_MS  = 5000;

  function injectCheckoutBtnStyle() {
    if (document.getElementById('cs-checkout-btn-style')) return;
    var s = document.createElement('style');
    s.id = 'cs-checkout-btn-style';
    s.textContent = [
      '.cs-checkout-syncing {',
      '  position: relative !important;',
      '  cursor: wait !important;',
      '  pointer-events: none !important;',
      '  opacity: 0.75 !important;',
      '}',
      '.cs-checkout-syncing .cs-checkout-label { visibility: hidden; }',
      '.cs-checkout-spinner {',
      '  display: none;',
      '  position: absolute; inset: 0;',
      '  align-items: center; justify-content: center;',
      '}',
      '.cs-checkout-syncing .cs-checkout-spinner { display: flex; }',
      '@keyframes cs-btn-spin { to { transform: rotate(360deg); } }',
      '.cs-checkout-spinner svg {',
      '  width: 1.8rem; height: 1.8rem;',
      '  animation: cs-btn-spin 0.8s linear infinite;',
      '  stroke: currentColor;',
      '}',
    ].join('\n');
    document.head.appendChild(s);
  }

  function getCheckoutBtns() {
    return document.querySelectorAll(
      '[name="checkout"], ' +
      '.cart__checkout-button, ' +
      '#CartDrawer-Checkout, ' +
      '.cart__ctas [type="submit"], ' +
      'cart-items button[type="submit"]'
    );
  }

  function setCheckoutBtnsLoading(loading) {
    // ── v4.8.1: safety watchdog ───────────────────────────────────────
    // Always clear any existing timer first.
    clearTimeout(_checkoutBtnSafetyTimer);
    _checkoutBtnSafetyTimer = null;

    if (loading) {
      // Start a 5-second watchdog — if sync hasn't finished by then,
      // force-restore the button so the user is never permanently blocked.
      _checkoutBtnSafetyTimer = setTimeout(function () {
        LOG.warn('Checkout button safety timeout (' + CHECKOUT_BTN_SAFETY_MS + 'ms) fired — force-restoring buttons');
        _checkoutBtnSafetyTimer = null;
        setCheckoutBtnsLoading(false);
      }, CHECKOUT_BTN_SAFETY_MS);
    }

    var btns = getCheckoutBtns();
    if (!btns.length) {
      LOG.info('setCheckoutBtnsLoading: no checkout buttons found yet (loading=' + loading + ')');
      return;
    }
    injectCheckoutBtnStyle();
    btns.forEach(function (btn) {
      if (loading) {
        if (!btn.querySelector('.cs-checkout-label')) {
          var label = document.createElement('span');
          label.className = 'cs-checkout-label';
          label.innerHTML = btn.innerHTML;
          btn.innerHTML = '';
          btn.appendChild(label);
          var spinner = document.createElement('span');
          spinner.className = 'cs-checkout-spinner';
          spinner.setAttribute('aria-hidden', 'true');
          spinner.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke-width="2.5" stroke-linecap="round"><path d="M12 2a10 10 0 1 0 10 10"/></svg>';
          btn.appendChild(spinner);
        }
        btn.classList.add('cs-checkout-syncing');
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-busy', 'true');
        LOG.info('setCheckoutBtnsLoading: ON — ' + (btn.id || btn.name || btn.className));
      } else {
        btn.classList.remove('cs-checkout-syncing');
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
        var labelEl = btn.querySelector('.cs-checkout-label');
        if (labelEl) btn.innerHTML = labelEl.innerHTML;
        LOG.info('setCheckoutBtnsLoading: OFF — ' + (btn.id || btn.name || btn.className));
      }
    });
  }

  // ── Write userId into Shopify cart attributes ─────────────────────────
  async function writeUserIdToCartAttributes() {
    LOG.info('writeUserIdToCartAttributes: stamping cs_user_id=' + USER_ID);
    try {
      isSyncingFromDO = true;
      var res  = await fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { cs_user_id: USER_ID } }),
      });
      var data = await res.json();
      if (data.status && data.status >= 400) {
        LOG.error('writeUserIdToCartAttributes: error', data);
      } else {
        var saved = (data.attributes && data.attributes.cs_user_id) || 'not found';
        LOG.ok('writeUserIdToCartAttributes done sent:' + USER_ID + ' saved:' + saved);
      }
    } catch (err) {
      LOG.error('writeUserIdToCartAttributes: failed', err);
    } finally {
      isSyncingFromDO = false;
    }
  }

  // ── Fetch cart from DO on arrival from WP checkout URL ───────────────
  async function fetchCartFromDO(forceApply) {
    LOG.info('fetchCartFromDO: fetching for userId:' + USER_ID + (forceApply ? ' (force)' : ''));
    try {
      var res  = await fetch(WORKER_URL + '/cart?uId=' + encodeURIComponent(USER_ID));
      var data = await res.json();

      var doCart    = data.cart          || [];
      var doHash    = data.cartHash      || computeCartHash(doCart);
      var doTs      = data.lastUpdatedAt || 0;
      var localHash = getLocalHash();
      var localTs   = getLocalTs();

      LOG.info('fetchCartFromDO doItems:' + doCart.length + ' doHash:' + doHash + ' doTs:' + doTs + ' localHash:' + localHash + ' localTs:' + localTs);

      if (!forceApply) {
        if (doHash === localHash) { LOG.ok('fetchCartFromDO: already in sync'); return; }
        if (doTs <= localTs && localHash !== 'empty') { LOG.warn('fetchCartFromDO: DO is older — keeping local'); return; }
      } else {
        if (doCart.length === 0 && localHash === 'empty') { LOG.ok('fetchCartFromDO: both empty, nothing to apply'); return; }
      }

      LOG.ok('fetchCartFromDO: applying DO cart items:' + doCart.length);
      applyCartToShopify(doCart, forceApply ? nowSeconds() : doTs, doHash);

    } catch (err) {
      LOG.error('fetchCartFromDO: failed', err);
    }
  }

  // ── Apply incoming cart to Shopify ────────────────────────────────────
  function applyCartToShopify(incomingItems, incomingTs, incomingHash) {
    setLocalMeta(incomingTs, incomingHash);
    if (isApplying) {
      LOG.warn('applyCartToShopify: queuing ts:' + incomingTs + ' hash:' + incomingHash);
      pendingApply = { items: incomingItems, ts: incomingTs, hash: incomingHash };
      return;
    }
    isApplying = true;
    showSyncStrip();
    setCheckoutBtnsLoading(true);
    _doApply(incomingItems, incomingTs, incomingHash);
  }

  // ── v4.6.0: variant error → silent in-place retry from DO ───────────
  function handleVariantError(errData) {
    LOG.warn('handleVariantError: variant/availability error from /cart/add.js', errData);

    if (!URL_USER_ID) {
      LOG.warn('handleVariantError: no URL_USER_ID, skipping DO retry');
      return false;
    }

    if (_applyRetryCount >= MAX_APPLY_RETRIES) {
      LOG.error('handleVariantError: max retries (' + MAX_APPLY_RETRIES + ') reached — giving up');
      setCheckoutBtnsLoading(false);
      hideSyncStrip(0);
      showCartToast([{ productName: 'Could not load your cart. Please refresh the page.' }]);
      return false;
    }

    _applyRetryCount++;
    LOG.warn('handleVariantError: retrying in-place (attempt ' + _applyRetryCount + '/' + MAX_APPLY_RETRIES + ') in 1.5s');

    // Update label text while spinner stays — safety timer is already running
    var btns = getCheckoutBtns();
    btns.forEach(function (btn) {
      var labelEl = btn.querySelector('.cs-checkout-label');
      if (labelEl) labelEl.textContent = 'Syncing\u2026';
    });

    setTimeout(function () {
      LOG.ok('handleVariantError: retrying fetchCartFromDO(true) attempt ' + _applyRetryCount);
      fetchCartFromDO(true);
    }, 1500);

    return true; // caller must return early — retry is in flight
  }

  async function _doApply(incomingItems, incomingTs, incomingHash) {
    LOG.recv('_doApply ' + incomingItems.length + ' items ts:' + incomingTs);
    isSyncingFromDO = true;

    try {
      // Step 1: Fetch current Shopify cart to compute a precise diff
      var currentCartRes = await fetch('/cart.js');
      var currentCart    = await currentCartRes.json();
      var currentItems   = currentCart.items || [];

      // Build map: variantId (string) → current qty in Shopify
      var currentMap = {};
      currentItems.forEach(function (item) {
        currentMap[String(item.variant_id)] = item.quantity;
      });

      // Build map: variantId (string) → desired qty from DO
      var desiredMap = {};
      incomingItems.forEach(function (item) {
        desiredMap[String(item.variantId)] = item.quantity;
      });

      // Step 2: Compute diff
      var toAdd = incomingItems.filter(function (item) {
        return !(String(item.variantId) in currentMap);
      }).map(function (item) {
        return { id: item.variantId, quantity: item.quantity };
      });

      var updates    = {};
      var hasUpdates = false;
      Object.keys(currentMap).forEach(function (varId) {
        var desired = desiredMap[varId];
        if (desired === undefined) {
          updates[varId] = 0; hasUpdates = true;
        } else if (desired !== currentMap[varId]) {
          updates[varId] = desired; hasUpdates = true;
        }
      });

      // Step 3: Skip entirely if nothing changed
      if (toAdd.length === 0 && !hasUpdates) {
        LOG.ok('_doApply: no changes needed — Shopify cart already matches DO state');
        await refreshThemeCart(incomingItems);
        return;
      }

      LOG.info('_doApply diff — toAdd:' + toAdd.length + ' updates:' + JSON.stringify(updates));

      // Step 4: Add new items
      if (toAdd.length > 0) {
        var addRes  = await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: toAdd }),
        });
        var addData = await addRes.json();

        if (addData.status && addData.status >= 400) {
          var desc    = (addData.description || '').toLowerCase();
          var msg     = (addData.message     || '').toLowerCase();
          var isVariantErr = addData.status === 422 || addData.status === 404 ||
            /variant|available|not found|invalid|no longer/.test(desc) ||
            /variant|available|not found/.test(msg);

          if (isVariantErr) {
            var navigating = handleVariantError(addData);
            if (navigating) {
              isSyncingFromDO = false;
              isApplying      = false;
              return;
            }
          }
          LOG.error('_doApply /cart/add.js error', addData);
        } else {
          LOG.ok('_doApply /cart/add.js added ' + toAdd.length + ' new items');
        }
      }

      // Step 5: Update quantities / remove items
      if (hasUpdates) {
        var updRes  = await fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates }),
        });
        var updData = await updRes.json();
        if (!updData.token) {
          LOG.error('_doApply /cart/update.js error', updData);
        } else {
          LOG.ok('_doApply /cart/update.js applied ' + Object.keys(updates).length + ' updates');
        }
      }

      LOG.ok('_doApply complete — refreshing cart UI');
      await refreshThemeCart(incomingItems);

    } catch (err) {
      LOG.error('_doApply failed', err);
    } finally {
      isSyncingFromDO      = false;
      isApplying           = false;
      lastApplyCompletedAt = Date.now();
      if (pendingApply) {
        var next = pendingApply; pendingApply = null;
        LOG.info('Applying queued state ts:' + next.ts);
        applyCartToShopify(next.items, next.ts, next.hash);
      }
    }
  }

  // ── Handle incoming WS message ────────────────────────────────────────
  function handleIncoming(msg) {
    if (msg.sourceId === SOURCE_ID) { LOG.info('Layer 1 blocked — own echo'); return; }
    var incomingHash = msg.cartHash || computeCartHash(msg.cart || []);
    var localHash    = getLocalHash();
    if (incomingHash === localHash) { LOG.info('Layer 2 blocked — identical hash ' + incomingHash); return; }
    var incomingTs = msg.lastUpdatedAt || 0;
    var localTs    = getLocalTs();
    if (incomingTs <= localTs) { LOG.warn('Layer 3 blocked — stale ts ' + incomingTs + ' <= ' + localTs); return; }
    LOG.recv('All 3 layers passed hash:' + localHash + '->' + incomingHash + ' ts:' + localTs + '->' + incomingTs);
    applyCartToShopify(msg.cart || [], incomingTs, incomingHash);
  }

  // ── Push Shopify cart to DO ───────────────────────────────────────────
  function scheduleSync(reason) {
    clearTimeout(syncDebounce);
    syncDebounce = setTimeout(function () { doSync(reason); }, 800);
  }

  async function doSync(reason) {
    if (isSyncingFromDO) { LOG.info('doSync skipped syncing (' + reason + ')'); return; }
    var msSince = Date.now() - lastApplyCompletedAt;
    if (msSince < SYNC_COOLDOWN_MS) { LOG.info('doSync skipped cooldown ' + Math.round(msSince) + 'ms (' + reason + ')'); return; }

    showSyncStrip();

    try {
      var res  = await fetch('/cart.js');
      var cart = await res.json();

      var items = (cart.items || []).map(function (item) {
        return {
          productId:   item.product_id,
          variantId:   item.variant_id,
          productName: item.title,
          price:       item.price / 100,
          quantity:    item.quantity,
          image:       (item.featured_image && item.featured_image.url) ? item.featured_image.url : (item.image || ''),
          variantName: item.variant_title || '',
          vendor:      item.vendor || '',
        };
      });

      var hash = computeCartHash(items);
      if (hash === getLocalHash()) { LOG.info('doSync: hash unchanged ' + hash + ' skipping'); return; }

      var ts = nowSeconds();
      setLocalMeta(ts, hash);

      var payload = { uId: USER_ID, sourceId: SOURCE_ID, cart: items, cartHash: hash, lastUpdatedAt: ts };
      LOG.push('doSync (' + reason + ') items:' + items.length + ' hash:' + hash);

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(Object.assign({ type: 'cart_update' }, payload)));
        LOG.push('Sent via WS');
        return;
      }

      var httpRes = await fetch(HTTP_UPDATE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      LOG.push('HTTP fallback response', await httpRes.json());

    } catch (err) { LOG.error('doSync failed', err); }
    finally { hideSyncStrip(600); }
  }

  // ── Refresh theme cart UI via Dawn Section Rendering API ──────────────
  async function refreshThemeCart(incomingItems) {
    LOG.ok('refreshThemeCart: fetching Dawn sections');

    var sectionIds = ['cart-icon-bubble', 'cart-live-region-text'];
    var mainCartItems  = document.getElementById('main-cart-items');
    var mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartItems  && mainCartItems.dataset.id)  sectionIds.push(mainCartItems.dataset.id);
    if (mainCartFooter && mainCartFooter.dataset.id) sectionIds.push(mainCartFooter.dataset.id);
    sectionIds.push('cart-drawer');

    try {
      var url    = window.location.pathname + '?sections=' + sectionIds.join(',');
      var res    = await fetch(url);
      var data   = await res.json();
      var parser = new DOMParser();

      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data['cart-icon-bubble']) {
        var parsed    = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
        var newBubble = parsed.querySelector('.cart-count-bubble');
        var oldBubble = bubble.querySelector('.cart-count-bubble');
        if (newBubble && oldBubble) { oldBubble.innerHTML = newBubble.innerHTML; }
        else { var sec = parsed.querySelector('.shopify-section'); if (sec) bubble.innerHTML = sec.innerHTML; }
      }

      var liveRegion = document.getElementById('cart-live-region-text') ||
                       document.getElementById('CartDrawer-LiveRegionText');
      if (liveRegion && data['cart-live-region-text']) {
        var parsed = parser.parseFromString(data['cart-live-region-text'], 'text/html');
        var sec    = parsed.querySelector('.shopify-section');
        if (sec) liveRegion.innerHTML = sec.innerHTML;
      }

      if (mainCartItems && mainCartItems.dataset.id && data[mainCartItems.dataset.id]) {
        var parsed      = parser.parseFromString(data[mainCartItems.dataset.id], 'text/html');
        var newContents = parsed.querySelector('.js-contents');
        var oldContents = mainCartItems.querySelector('.js-contents');
        if (newContents && oldContents) { oldContents.innerHTML = newContents.innerHTML; }
        else { var ns = parsed.querySelector('#main-cart-items'); if (ns) mainCartItems.innerHTML = ns.innerHTML; }
      }

      if (mainCartFooter && mainCartFooter.dataset.id && data[mainCartFooter.dataset.id]) {
        var parsed      = parser.parseFromString(data[mainCartFooter.dataset.id], 'text/html');
        var newContents = parsed.querySelector('.js-contents');
        var oldContents = mainCartFooter.querySelector('.js-contents');
        if (newContents && oldContents) { oldContents.innerHTML = newContents.innerHTML; }
        else { var ns = parsed.querySelector('#main-cart-footer'); if (ns) mainCartFooter.innerHTML = ns.innerHTML; }
      }

      var cartDrawer = document.getElementById('CartDrawer');
      if (cartDrawer && data['cart-drawer']) {
        var parsed      = parser.parseFromString(data['cart-drawer'], 'text/html');
        var drawerInner = cartDrawer.querySelector('.drawer__inner') || cartDrawer;
        var newInner    = parsed.querySelector('#CartDrawer .drawer__inner') ||
                          parsed.querySelector('.drawer__inner') ||
                          parsed.querySelector('.shopify-section');
        if (newInner) {
          drawerInner.innerHTML = newInner.innerHTML;
          var updatedDrawer = parsed.querySelector('#CartDrawer');
          if (updatedDrawer) {
            cartDrawer.classList.toggle('is-empty', updatedDrawer.classList.contains('is-empty'));
          }
        }
      }

      if (mainCartItems) {
        fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
          var isEmpty = c.item_count === 0;
          var wrapper = document.querySelector('cart-items');
          if (wrapper) wrapper.classList.toggle('is-empty', isEmpty);
          if (mainCartFooter) mainCartFooter.classList.toggle('is-empty', isEmpty);
        });
      }

      LOG.ok('refreshThemeCart: complete (' + sectionIds.length + ' sections)');

      // Re-enable checkout buttons after DOM refresh
      setTimeout(function () {
        setCheckoutBtnsLoading(false);
        // Belt-and-suspenders: also clear any freshly rendered buttons
        document.querySelectorAll(
          '#main-cart-footer [name="checkout"], #main-cart-footer button[type="submit"], ' +
          '.cart__checkout-button, cart-items button[type="submit"]'
        ).forEach(function (btn) {
          btn.removeAttribute('disabled');
          btn.classList.remove('loading', 'loading--spinner', 'cs-checkout-syncing');
        });
        var cartForm = document.querySelector('form[action="/cart"]');
        if (cartForm) cartForm.dispatchEvent(new Event('change', { bubbles: true }));
        LOG.ok('refreshThemeCart: checkout buttons re-enabled');
      }, 100);

      hideSyncStrip(600);
      if (!URL_USER_ID) showCartToast(incomingItems || []);

    } catch (err) {
      LOG.warn('refreshThemeCart: section API failed — falling back to reload', err);
      setCheckoutBtnsLoading(false);
      hideSyncStrip(0);
      window.location.reload();
    }
  }

  // ── Intercept Shopify cart mutations ──────────────────────────────────
  var _origFetch = window.fetch;
  window.fetch = async function () {
    var response = await _origFetch.apply(this, arguments);
    var url = String(arguments[0] || '');
    if (url.includes('/cart/add') || url.includes('/cart/update') ||
        url.includes('/cart/change') || url.includes('/cart/clear')) {
      if (isSyncingFromDO) {
        LOG.info('Fetch interceptor: suppressed ' + url);
      } else {
        var type = url.split('/cart/')[1].split('.')[0].split('?')[0];
        LOG.info('Fetch interceptor: user mutation -> ' + type);
        setTimeout(function () { scheduleSync('mutation:' + type); }, 300);
      }
    }
    return response;
  };

  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest('[name="checkout"], .cart__checkout-button, [href="/checkout"]')) {
      LOG.info('Checkout click — stamping attribute + pushing cart');
      writeUserIdToCartAttributes();
      doSync('checkout_started');
    }
  });

  // ── WebSocket ─────────────────────────────────────────────────────────
  function connect() {
    if (isStopped) return;
    var url = WS_URL + '?uId=' + encodeURIComponent(USER_ID) + '&sourceId=' + encodeURIComponent(SOURCE_ID);
    LOG.info('Connecting WS ' + url);
    if (ws) { try { ws.close(); } catch (e) {} }
    ws = new WebSocket(url);

    ws.addEventListener('open', function () {
      LOG.ok('WS connected ts:' + getLocalTs() + ' hash:' + getLocalHash());
      reconnectDelay = 2000;
      scheduleSync('page_load');
    });

    ws.addEventListener('message', function (event) {
      try {
        var msg = JSON.parse(event.data);
        if (msg.type === 'cart_init' || msg.type === 'cart_update') {
          handleIncoming(msg);
        } else if (msg.type === 'checkout_completed') {
          LOG.ok('checkout_completed — stopping WS');
          isStopped = true;
          ws.close(1000, 'Checkout completed');
        } else if (msg.type !== 'pong') {
          LOG.warn('Unknown WS type: ' + msg.type);
        }
      } catch (e) { LOG.error('WS parse error', e); }
    });

    ws.addEventListener('close', function (event) {
      if (event.code === 1000) return;
      setTimeout(connect, reconnectDelay);
      reconnectDelay = Math.min(reconnectDelay * 1.5, 30000);
    });

    ws.addEventListener('error', function (e) { LOG.error('WS error', e); ws.close(); });
  }

  // ── Patch all WordPress links — append ?uId= on click ────────────────
  var _wpLinksPatched = false;
  function patchWPLinks() {
    if (_wpLinksPatched) return;
    _wpLinksPatched = true;
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href) return;
      var isWPLink = href.startsWith(WP_DOMAIN) || href.startsWith(WP_DOMAIN.replace('https://', '//'));
      if (!isWPLink) return;
      if (href.includes('uId=') || href.includes('userId=')) return;
      e.preventDefault();
      var currentUserId = localStorage.getItem(LS_USER_ID_KEY) || USER_ID;
      var target = new URL(href);
      target.searchParams.set('uId', currentUserId);
      LOG.info('WP link patched uId:' + currentUserId + ' -> ' + target.toString());
      window.location.href = target.toString();
    });
    LOG.ok('patchWPLinks: active for ' + WP_DOMAIN);
  }

  // ── Sync strip / Toast ────────────────────────────────────────────────
  var _stripEl    = null;
  var _stripTimer = null;
  var _toastEl    = null;
  var _toastTimer = null;
  var TOAST_DURATION_MS = 4000;

  function injectSyncStripStyles() {
    if (document.getElementById('cs-sync-strip-styles')) return;
    var s = document.createElement('style');
    s.id = 'cs-sync-strip-styles';
    s.textContent = [
      '#cs-sync-strip {',
      '  position: fixed; top: 0; left: 0; right: 0; z-index: 99998;',
      '  height: 3px; background: rgba(var(--color-foreground, 18,18,18), 0.12);',
      '  overflow: hidden; transition: opacity 0.3s ease;',
      '}',
      '#cs-sync-strip::after {',
      '  content: ""; position: absolute; top: 0; left: -40%;',
      '  width: 40%; height: 100%;',
      '  background: rgb(var(--color-foreground, 18,18,18));',
      '  animation: cs-strip-slide 1.1s ease-in-out infinite;',
      '}',
      '#cs-sync-strip.cs-strip-hidden { opacity: 0; pointer-events: none; }',
      '@keyframes cs-strip-slide { 0% { left: -40%; } 100% { left: 120%; } }',
    ].join('\n');
    document.head.appendChild(s);
  }

  function injectToastStyles() {
    if (document.getElementById('cs-toast-styles')) return;
    var s = document.createElement('style');
    s.id = 'cs-toast-styles';
    s.textContent = [
      '#cs-toast {',
      '  position: fixed; bottom: 2.4rem; right: 2.4rem; z-index: 99999;',
      '  display: flex; align-items: flex-start; gap: 1.2rem;',
      '  padding: 1.6rem 2rem; min-width: 26rem; max-width: 36rem;',
      '  background: rgb(var(--color-background, 255,255,255));',
      '  color: rgb(var(--color-foreground, 18,18,18));',
      '  font-family: var(--font-body-family, sans-serif); font-size: 1.3rem;',
      '  border-radius: var(--popup-corner-radius, 0.4rem);',
      '  border: var(--popup-border-width, 1px) solid rgba(var(--color-foreground, 18,18,18), var(--popup-border-opacity, 0.1));',
      '  box-shadow: var(--popup-shadow-horizontal-offset, 0px) var(--popup-shadow-vertical-offset, 4px) var(--popup-shadow-blur-radius, 16px) rgba(var(--color-shadow, 0,0,0), var(--popup-shadow-opacity, 0.1));',
      '  transform: translateY(1.2rem); opacity: 0;',
      '  transition: opacity 0.25s ease, transform 0.25s ease; pointer-events: none;',
      '}',
      '#cs-toast.cs-toast--visible { opacity: 1; transform: translateY(0); pointer-events: auto; }',
      '#cs-toast.cs-toast--hiding  { opacity: 0; transform: translateY(1.2rem); }',
      '#cs-toast .cs-toast__icon   { flex-shrink: 0; width: 1.8rem; height: 1.8rem; margin-top: 0.1rem; }',
      '#cs-toast .cs-toast__body   { flex: 1; line-height: 1.5; }',
      '#cs-toast .cs-toast__title  { font-weight: 600; margin: 0 0 0.2rem; font-size: 1.3rem; letter-spacing: 0.02em; }',
      '#cs-toast .cs-toast__message{ margin: 0; opacity: 0.65; font-size: 1.2rem; }',
      '#cs-toast .cs-toast__close  { flex-shrink: 0; background: none; border: none; cursor: pointer; padding: 0; margin-top: -0.2rem; color: rgb(var(--color-foreground, 18,18,18)); opacity: 0.4; line-height: 1; transition: opacity 0.15s ease; }',
      '#cs-toast .cs-toast__close:hover { opacity: 0.85; }',
      '#cs-toast .cs-toast__progress { position: absolute; bottom: 0; left: 0; height: 2px; width: 100%; background: rgba(var(--color-foreground, 18,18,18), 0.15); border-radius: 0 0 var(--popup-corner-radius, 0.4rem) var(--popup-corner-radius, 0.4rem); transform-origin: left; transform: scaleX(1); }',
      '@media screen and (max-width: 749px) { #cs-toast { left: 1.6rem; right: 1.6rem; min-width: unset; max-width: unset; bottom: 1.6rem; } }',
    ].join('\n');
    document.head.appendChild(s);
  }

  function showSyncStrip() {
    injectSyncStripStyles();
    if (_stripEl) { clearTimeout(_stripTimer); _stripEl.classList.remove('cs-strip-hidden'); return; }
    _stripEl = document.createElement('div');
    _stripEl.id = 'cs-sync-strip';
    _stripEl.setAttribute('role', 'status');
    _stripEl.setAttribute('aria-label', 'Updating cart\u2026');
    document.body.appendChild(_stripEl);
    LOG.ok('Sync strip shown');
  }

  function hideSyncStrip(delay) {
    if (!_stripEl) return;
    clearTimeout(_stripTimer);
    _stripTimer = setTimeout(function () {
      if (!_stripEl) return;
      _stripEl.classList.add('cs-strip-hidden');
      var el = _stripEl;
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 320);
      _stripEl = null;
      LOG.ok('Sync strip hidden');
    }, delay || 600);
  }

  function showCartToast(items) {
    injectToastStyles();
    if (_toastEl) {
      clearTimeout(_toastTimer);
      if (_toastEl.parentNode) _toastEl.parentNode.removeChild(_toastEl);
      _toastEl = null;
    }
    var title   = 'Cart updated';
    var message = '';
    if (!items || items.length === 0) {
      message = 'Your cart has been cleared.';
    } else if (items.length === 1) {
      message = items[0].productName ? '\u2018' + items[0].productName + '\u2019 is in your cart.' : '1 item in your cart.';
    } else {
      message = items.length + ' items synced to your cart.';
    }
    var checkIcon = '<svg class="cs-toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
    var closeIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    _toastEl = document.createElement('div');
    _toastEl.id = 'cs-toast';
    _toastEl.setAttribute('role', 'status');
    _toastEl.setAttribute('aria-live', 'polite');
    _toastEl.setAttribute('aria-atomic', 'true');
    _toastEl.innerHTML = [
      checkIcon,
      '<div class="cs-toast__body">',
      '  <p class="cs-toast__title">' + title + '</p>',
      '  <p class="cs-toast__message">' + message + '</p>',
      '</div>',
      '<button class="cs-toast__close" aria-label="Dismiss">' + closeIcon + '</button>',
      '<div class="cs-toast__progress"></div>',
    ].join('');
    document.body.appendChild(_toastEl);
    _toastEl.querySelector('.cs-toast__close').addEventListener('click', function () { hideCartToast(true); });
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { if (_toastEl) _toastEl.classList.add('cs-toast--visible'); });
    });
    var progressEl = _toastEl.querySelector('.cs-toast__progress');
    if (progressEl) {
      progressEl.style.transition = 'transform ' + TOAST_DURATION_MS + 'ms linear';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { if (progressEl) progressEl.style.transform = 'scaleX(0)'; });
      });
    }
    _toastTimer = setTimeout(function () { hideCartToast(false); }, TOAST_DURATION_MS);
    LOG.ok('Toast shown: ' + title + ' — ' + message);
  }

  function hideCartToast(immediate) {
    if (!_toastEl) return;
    clearTimeout(_toastTimer);
    _toastEl.classList.remove('cs-toast--visible');
    _toastEl.classList.add('cs-toast--hiding');
    var el = _toastEl;
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 260);
    _toastEl = null;
    LOG.ok('Toast hidden');
  }

  // ── Boot ──────────────────────────────────────────────────────────────
  function boot() {
    LOG.ok('boot() URL_USER_ID:' + (URL_USER_ID || 'none') + ' CS_RETRY:' + CS_RETRY);

    if (URL_USER_ID) {
      var forceApply = true;
      LOG.ok('Arrived via WP — forceApply:true | showing sync strip + button loading');
      showSyncStrip();
      setCheckoutBtnsLoading(true);
      writeUserIdToCartAttributes()
        .then(function () { return fetchCartFromDO(forceApply); })
        .then(function () {
          if (!isApplying) {
            setCheckoutBtnsLoading(false);
            hideSyncStrip(300);
          }
          connect();
        })
        .catch(function (err) {
          LOG.error('Arrival sequence failed', err);
          setCheckoutBtnsLoading(false);
          hideSyncStrip(0);
          connect();
        });
    } else {
      showSyncStrip();
      writeUserIdToCartAttributes();
      connect();
    }

    patchWPLinks();
  }

  document.addEventListener('DOMContentLoaded', function () {
    LOG.ok('DOMContentLoaded — running boot()');
    boot();
  });

  // ── bfcache recovery ──────────────────────────────────────────────────
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    if (!URL_USER_ID) return;
    if (window.location.pathname.indexOf('/cart') === -1) return;
    LOG.ok('pageshow persisted=true on cart+uId — reloading for fresh DO fetch');
    window.location.href = window.location.href;
  });

  window.CartSyncShopify = {
    userId:      USER_ID,
    sourceId:    SOURCE_ID,
    csRetry:     CS_RETRY,
    push:        function () { doSync('manual'); },
    reconnect:   connect,
    fetchFromDO: fetchCartFromDO,
    refreshUI:   function () { return refreshThemeCart([]); },
    toast:       function (msg) { showCartToast(msg ? [{ productName: msg }] : []); },
    hash: function () {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
        LOG.info('Shopify cart hash: ' + computeCartHash(c.items || []));
      });
    },
    status: function () {
      LOG.info('status', {
        wsState:         ws ? ['CONNECTING','OPEN','CLOSING','CLOSED'][ws.readyState] : 'null',
        userId:          USER_ID,
        sourceId:        SOURCE_ID,
        urlUserId:       URL_USER_ID,
        csRetry:         CS_RETRY,
        localTs:         getLocalTs(),
        localHash:       getLocalHash(),
        isSyncingFromDO: isSyncingFromDO,
        isApplying:      isApplying,
      });
    },
  };

  LOG.ok('Ready — CartSyncShopify.status() | .hash() | .fetchFromDO() | .refreshUI() | .toast()');

})();
